import type React from "react";
import {
  Accessibility,
  Ban,
  Building2,
  Calendar,
  Check,
  ExternalLink,
  Instagram,
  Link2,
  MapPin,
  Radio,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Ticket,
  Users,
} from "lucide-react";
import type {
  EventPageView,
  PublicEventEntry,
} from "../../../src/eventPage.ts";
import type { EventEntry } from "../../../src/fields.ts";
import type { Language } from "../../../src/texts.ts";
import { eventPageUiTexts } from "../../../src/eventPageTexts.ts";
import { buildClientEventView } from "../eventUtils.ts";

export interface EventCardProps {
  event: EventEntry | PublicEventEntry;
  view?: EventPageView;
  language?: Language;
  tasteVote?: "like" | "dislike" | null;
  onTasteVote?: (direction: "like" | "dislike") => void;
  tasteLoading?: boolean;
  showTasteControls?: boolean;
  onSignInRequired?: () => void;
  headerAction?: React.ReactNode;
  className?: string;
  maxPosterHeight?: string;
}

export const SectionTitle = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => (
  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-purple-400 mb-2.5">
    {icon}
    {label}
  </div>
);

export const GoogleMapsLogo = ({
  className = "w-5 h-5",
}: {
  className?: string;
}) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C7.58 2 4 5.58 4 10c0 5.8 6.55 11.23 7.39 11.9.36.29.86.29 1.22 0 .84-.67 7.39-6.1 7.39-11.9 0-4.42-3.58-8-8-8z"
      fill="#EA4335"
    />
    <path
      d="M12 2C7.58 2 4 5.58 4 10c0 2.21.9 4.21 2.34 5.66L12 7.5V2z"
      fill="#4285F4"
    />
    <path
      d="M6.34 15.66A7.95 7.95 0 0 0 12 18v-8.5L6.34 15.66z"
      fill="#34A853"
    />
    <path
      d="M12 18c2.21 0 4.21-.9 5.66-2.34L12 9.5V18z"
      fill="#FBBC04"
    />
    <circle cx="12" cy="10" r="3" fill="#FFFFFF" />
  </svg>
);

export const GoogleCalendarLogo = ({
  className = "w-5 h-5",
}: {
  className?: string;
}) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 002 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"
      fill="#4285F4"
    />
    <path d="M5 6h14v3H5z" fill="#EA4335" />
    <path d="M8 12h3v3H8z" fill="#34A853" />
    <path d="M13 12h3v3h-3z" fill="#FBBC04" />
    <path d="M8 16h3v3H8z" fill="#4285F4" />
    <path d="M13 16h3v3h-3z" fill="#EA4335" />
  </svg>
);

export const WhatsAppLogo = ({
  className = "w-4 h-4",
}: {
  className?: string;
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const InfoCard = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 transition-colors hover:bg-white/[0.05] hover:border-white/15">
    <SectionTitle icon={icon} label={label} />
    <div
      dir="auto"
      className="text-[15px] font-medium break-words text-gray-200"
    >
      {children}
    </div>
  </div>
);

export const EventCard = ({
  event,
  view: explicitView,
  language = "en",
  tasteVote,
  onTasteVote,
  tasteLoading = false,
  showTasteControls = false,
  onSignInRequired,
  headerAction,
  className = "",
  maxPosterHeight = "max-h-[520px] md:max-h-[620px]",
}: EventCardProps) => {
  const view = explicitView ?? buildClientEventView(event, language);
  const startTime = view.startTime ??
    (view.timeText?.includes("–")
      ? view.timeText.split("–")[0].trim()
      : view.timeText);
  const endTime = view.endTime ??
    (view.timeText?.includes("–")
      ? view.timeText.split("–")[1].trim()
      : undefined);
  const { name, description, posterUrl, videoUrl, coupon, links, performers } =
    event.structured;
  const texts = eventPageUiTexts[language];

  const recommendationScore = (event as EventEntry).recommendationScore;

  return (
    <div
      dir="ltr"
      className={`glass-card overflow-hidden transition-all duration-300 ${className}`}
    >
      {posterUrl && (
        <div className="relative w-full overflow-hidden bg-black/50 flex items-center justify-center min-h-[200px]">
          <div
            className="absolute inset-0 bg-cover bg-center blur-2xl opacity-35 scale-110 pointer-events-none"
            style={{ backgroundImage: `url(${posterUrl})` }}
            aria-hidden="true"
          />
          <img
            src={posterUrl}
            alt={`${name} poster`}
            className={`relative z-10 w-full h-full object-contain ${maxPosterHeight}`}
          />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#141414]/90 to-transparent pointer-events-none z-10" />
        </div>
      )}

      <div className="p-6 md:p-8 space-y-6">
        {/* Top Badges & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex flex-wrap items-center gap-2">
            {recommendationScore !== undefined && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/35 shadow-sm shadow-amber-500/10">
                ✨ {Math.round(recommendationScore * 100)}% {texts.matchScore}
              </span>
            )}
            {(event as EventEntry).interestedFriends &&
              (event as EventEntry).interestedFriends!.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-200 border border-purple-500/35 shadow-sm shadow-purple-500/10">
                <Users className="w-3.5 h-3.5 text-purple-300" />
                {(event as EventEntry).interestedFriends!.join(", ")}{" "}
                {language === "he" ? "מתעניינים" : "interested"}
              </span>
            )}
            {view.categories.map(({ emoji, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-purple-300 bg-purple-500/10 border border-purple-500/25"
              >
                {`${emoji} ${label}`.trim()}
              </span>
            ))}
            {(view.isSoldOut || event.structured.soldOut) && (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40">
                <Ban className="w-3.5 h-3.5" />
                {texts.soldOut}
              </span>
            )}
          </div>
          {headerAction && <div className="shrink-0">{headerAction}</div>}
        </div>

        {/* Title & Subtitle */}
        <div>
          <h1
            dir="auto"
            className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-2 tracking-tight"
          >
            {name}
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">
            {view.isMultiDay && view.endDayName && view.endDateText
              ? (
                <>
                  <strong className="text-purple-200 font-semibold">
                    {view.dayName}
                  </strong>{" "}
                  {view.dateText}
                  {startTime ? ` ${startTime}` : ""} –{" "}
                  <strong className="text-purple-200 font-semibold">
                    {view.endDayName}
                  </strong>{" "}
                  {view.endDateText}
                  {endTime ? ` ${endTime}` : ""}
                </>
              )
              : (
                <>
                  <strong className="text-purple-200 font-semibold">
                    {view.dayName}
                  </strong>{" "}
                  {view.dateText} · {view.timeText}
                </>
              )}
          </p>
        </div>

        {/* Optional Interactive Taste Controls Bar */}
        {showTasteControls && (
          <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-bold uppercase tracking-wider text-purple-300 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-purple-400" />
              {texts.tastePrompt}
            </div>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => {
                  if (onTasteVote) onTasteVote("like");
                  else if (onSignInRequired) onSignInRequired();
                }}
                disabled={tasteLoading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all active:scale-95 duration-150 disabled:opacity-50 ${
                  tasteVote === "like"
                    ? "bg-emerald-500/30 border-2 border-emerald-400 text-emerald-100 shadow-md shadow-emerald-500/20 ring-2 ring-emerald-500/30"
                    : "bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 hover:text-emerald-200"
                }`}
              >
                {tasteVote === "like"
                  ? <Check className="w-4 h-4 text-emerald-300" />
                  : <ThumbsUp className="w-4 h-4" />}
                {tasteVote === "like"
                  ? texts.savedToMyEvents
                  : texts.saveToMyEvents}
              </button>

              <button
                type="button"
                onClick={() => {
                  if (onTasteVote) onTasteVote("dislike");
                  else if (onSignInRequired) onSignInRequired();
                }}
                disabled={tasteLoading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all active:scale-95 duration-150 disabled:opacity-50 ${
                  tasteVote === "dislike"
                    ? "bg-rose-500/30 border-2 border-rose-400 text-rose-100 shadow-md shadow-rose-500/20 ring-2 ring-rose-500/30"
                    : "bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 hover:text-rose-200"
                }`}
              >
                {tasteVote === "dislike"
                  ? <Check className="w-4 h-4 text-rose-300" />
                  : <ThumbsDown className="w-4 h-4" />}
                {tasteVote === "dislike" ? texts.passedEvent : texts.passEvent}
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        {description && (
          <p
            dir="auto"
            className="text-gray-300 whitespace-pre-wrap break-words text-[15px] leading-relaxed"
          >
            {description}
          </p>
        )}

        {/* Info Cards Grid */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          <InfoCard
            icon={<Calendar className="w-3.5 h-3.5" />}
            label={texts.dateAndTime}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                {view.isMultiDay && view.endDayName && view.endDateText
                  ? (
                    <>
                      <div>
                        {view.dayName} {view.dateText}
                        {startTime ? ` ${startTime}` : ""} –
                      </div>
                      <div>
                        {view.endDayName} {view.endDateText}
                        {endTime ? ` ${endTime}` : ""}
                      </div>
                    </>
                  )
                  : (
                    <>
                      {view.dayName}, {view.dateText}
                      <br />
                      {view.timeText}
                    </>
                  )}
              </div>
              {view.calendarUrl && (
                <a
                  href={view.calendarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={texts.addToCalendar}
                  title={texts.addToCalendar}
                  className="shrink-0 inline-flex items-center justify-center p-1 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <GoogleCalendarLogo className="w-5 h-5" />
                </a>
              )}
            </div>
          </InfoCard>
          <InfoCard
            icon={<MapPin className="w-3.5 h-3.5" />}
            label={texts.location}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="flex-1" dir="auto">{view.locationText}</span>
              {view.mapsUrl && (
                <a
                  href={view.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={texts.openInGoogleMaps}
                  title={texts.openInGoogleMaps}
                  className="shrink-0 inline-flex items-center justify-center p-1 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <GoogleMapsLogo className="w-5 h-5" />
                </a>
              )}
            </div>
          </InfoCard>
          {view.city && (
            <InfoCard
              icon={<Building2 className="w-3.5 h-3.5" />}
              label={texts.city}
            >
              {view.city}
            </InfoCard>
          )}
          {view.priceText && (
            <InfoCard
              icon={<Ticket className="w-3.5 h-3.5" />}
              label={texts.price}
            >
              {view.priceText}
            </InfoCard>
          )}
          {view.accessibilityText && (
            <InfoCard
              icon={<Accessibility className="w-3.5 h-3.5" />}
              label={texts.accessibility}
            >
              {view.accessibilityText}
            </InfoCard>
          )}
        </div>

        {/* Lineup Section */}
        {performers && performers.length > 0 && (
          <div>
            <SectionTitle
              icon={<Sparkles className="w-3.5 h-3.5" />}
              label={texts.lineup}
            />
            <div className="flex flex-wrap gap-2">
              {performers.map((performer) => (
                <span
                  key={performer}
                  dir="auto"
                  className="px-3.5 py-1.5 rounded-full text-sm font-semibold bg-white/5 border border-white/10 text-gray-200"
                >
                  {performer}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Coupon */}
        {coupon && (
          <div
            dir="auto"
            className="flex items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed border-pink-500/50 bg-pink-500/10 text-pink-300 font-bold p-4"
          >
            <Ticket className="w-5 h-5 shrink-0" />
            {`${texts.coupon}: ${coupon}`}
          </div>
        )}

        {/* Video Player */}
        {videoUrl && (
          <video
            src={videoUrl}
            controls
            preload="none"
            className="w-full rounded-2xl border border-white/10"
          />
        )}

        {/* Source Attribution */}
        {view.source && (
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
            <SectionTitle
              icon={<Radio className="w-3.5 h-3.5" />}
              label={texts.source}
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm font-semibold text-purple-300">
                {view.source.url
                  ? (
                    <a
                      href={view.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-purple-300 hover:text-purple-200 underline underline-offset-4"
                    >
                      {view.source.kind === "instagram" && (
                        <Instagram className="w-4 h-4 text-pink-400 shrink-0" />
                      )}
                      {view.source.kind === "whatsapp" && (
                        <WhatsAppLogo className="w-4 h-4 text-emerald-400 shrink-0" />
                      )}
                      {view.source.label && <span>{view.source.label}</span>}
                      <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                    </a>
                  )
                  : (
                    <span className="inline-flex items-center gap-1.5">
                      {view.source.kind === "instagram" && (
                        <Instagram className="w-4 h-4 text-pink-400 shrink-0" />
                      )}
                      {view.source.kind === "whatsapp" && (
                        <WhatsAppLogo className="w-4 h-4 text-emerald-400 shrink-0" />
                      )}
                      {view.source.label && <span>{view.source.label}</span>}
                    </span>
                  )}
              </span>
            </div>
            {view.source.originalText &&
              view.source.originalText !== description && (
              <details className="mt-3 text-xs text-gray-400">
                <summary className="cursor-pointer font-medium text-purple-300/80 hover:text-purple-200">
                  {texts.originalTextLabel}
                </summary>
                <p
                  dir="auto"
                  className="mt-2 whitespace-pre-wrap rounded-lg bg-black/30 p-3 text-gray-300 border border-white/5"
                >
                  {view.source.originalText}
                </p>
              </details>
            )}
          </div>
        )}

        {/* Ticket Links List */}
        {links.length > 0 && (
          <div>
            <SectionTitle
              icon={<Link2 className="w-3.5 h-3.5" />}
              label={texts.links}
            />
            <div className="flex flex-col gap-2">
              {links.map((link) => (
                <a
                  key={link}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-gray-200 hover:border-purple-500/50 hover:bg-purple-500/10 transition-colors break-all"
                >
                  <ExternalLink className="w-4 h-4 shrink-0 text-purple-400" />
                  {link}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
